'use client'

import { useState, useEffect } from 'react'
import { Menu, X, Phone, Mail, Clock } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import navigation from '@/data/navigation.json'
import { WhatsAppIcon } from './WhatsAppIcon'
import { getWhatsAppUrl, PHONE_CALL_URL } from '@/lib/whatsapp'

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const pathname = usePathname()

  const generalWhatsAppUrl = getWhatsAppUrl({ type: 'general' })

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 60)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  return (
    <>
      {/* Top Bar (hidden on mobile, visible md+) */}
      <div className="hidden md:block bg-[#EFF6FF] border-b border-[#DBEAFE]">
        <div className="container-narrow py-2 flex justify-between items-center text-xs">
          <div className="flex gap-5 items-center text-[#475569]">
            <a
              id="topbar-call-link"
              href={PHONE_CALL_URL}
              className="flex items-center gap-1.5 hover:text-[#1D4ED8] transition-colors duration-200 font-medium"
            >
              <Phone className="w-3 h-3 text-[#1D4ED8]" />
              {navigation.topBar.phone}
            </a>
            <a
              id="topbar-email-link"
              href={`mailto:${navigation.topBar.email}`}
              className="flex items-center gap-1.5 hover:text-[#1D4ED8] transition-colors duration-200 font-medium truncate max-w-xs"
            >
              <Mail className="w-3 h-3 text-[#1D4ED8] flex-shrink-0" />
              {navigation.topBar.email}
            </a>
          </div>
          <div className="flex items-center gap-1.5 text-[#475569] font-medium">
            <Clock className="w-3 h-3 text-[#D97706]" />
            {navigation.topBar.workingHours}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`transition-all duration-300 border-b border-[#DBEAFE] ${
          isSticky
            ? 'fixed top-0 left-0 right-0 z-50 bg-white/98 backdrop-blur-md shadow-sm'
            : 'sticky top-0 z-40 bg-white'
        }`}
        style={isSticky ? { boxShadow: '0 2px 16px rgba(29,78,216,0.09)' } : {}}
      >
        <div className="container-narrow flex items-center justify-between py-3 gap-3">
          {/* Logo */}
          <Link href="/" className="flex flex-col flex-shrink-0 group">
            <span className="text-lg font-extrabold text-[#1E40AF] group-hover:text-[#1D4ED8] transition-colors duration-200 leading-none">
              {navigation.logo.text}
            </span>
            <span className="text-[10px] text-[#B45309] font-semibold tracking-wide leading-none mt-0.5">
              {navigation.logo.tagline}
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {navigation.navItems.map((item) => {
              const isActive = pathname === item.href
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  aria-label={item.ariaLabel}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-[#1D4ED8] text-white'
                      : 'text-[#475569] hover:text-[#1D4ED8] hover:bg-[#EFF6FF]'
                  }`}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <a
              id="navbar-call-btn"
              href={PHONE_CALL_URL}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#DBEAFE] text-[#1D4ED8] hover:bg-[#EFF6FF] transition-all duration-200 text-xs font-semibold"
              aria-label="Call us"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden xl:inline">Call</span>
            </a>

            {/* Authentic Green WhatsApp Button */}
            <a
              id="navbar-whatsapp-btn"
              href={generalWhatsAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-bold text-xs shadow-sm transition-all duration-200 hover:brightness-105 active:scale-95"
              style={{ backgroundColor: '#25D366' }}
              aria-label="Chat on WhatsApp"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <WhatsAppIcon className="w-3.5 h-3.5 fill-white" />
              <span>WhatsApp</span>
            </a>

            <Link
              id="navbar-book-btn"
              href="/contact"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#1D4ED8] text-white hover:bg-[#1E40AF] transition-all duration-200 text-xs font-semibold"
              style={{ boxShadow: '0 2px 8px rgba(29,78,216,0.25)' }}
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Direct Header Actions: Instant WhatsApp + Menu Toggle */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Compact direct 1-tap WhatsApp button right in header */}
            <a
              id="mobile-nav-whatsapp-btn"
              href={generalWhatsAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white font-bold text-xs shadow-sm"
              style={{ backgroundColor: '#25D366' }}
              aria-label="Direct WhatsApp Chat"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              <WhatsAppIcon className="w-3.5 h-3.5 fill-white" />
              <span>WhatsApp</span>
            </a>

            {/* Menu Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-1.5 rounded-xl hover:bg-[#EFF6FF] transition-colors duration-200 text-[#1E40AF]"
              aria-label="Toggle navigation menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu Drawer */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-[#DBEAFE] shadow-xl">
            <div className="container-narrow py-3 flex flex-col gap-1">
              {navigation.navItems.map((item) => {
                const isActive = pathname === item.href
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    aria-label={item.ariaLabel}
                    className={`px-4 py-2.5 rounded-xl transition-all duration-200 font-medium text-sm ${
                      isActive
                        ? 'bg-[#EFF6FF] text-[#1D4ED8] font-semibold'
                        : 'text-[#475569] hover:bg-[#F8FBFF] hover:text-[#1D4ED8]'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              })}

              <div className="grid grid-cols-2 gap-2 pt-3 mt-1 border-t border-[#DBEAFE]">
                <a
                  id="mobile-drawer-call-btn"
                  href={PHONE_CALL_URL}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#DBEAFE] text-[#1D4ED8] text-xs font-bold bg-[#EFF6FF]"
                >
                  <Phone className="w-3.5 h-3.5" /> Call Us
                </a>
                <a
                  id="mobile-drawer-whatsapp-btn"
                  href={generalWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-xs font-bold"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <WhatsAppIcon className="w-3.5 h-3.5 fill-white" /> WhatsApp
                </a>
                <Link
                  id="mobile-drawer-book-btn"
                  href="/contact"
                  className="col-span-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1D4ED8] text-white text-xs font-bold"
                >
                  Book Free Consultation
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  )
}
